import { useSelector } from '@xstate/react';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMount } from 'react-use';

import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Switch,
  Text,
  Textarea,
  cn,
} from '@eluve/components';
import { EluveEditor } from '@eluve/eluve-editor';
import { useTranslation } from '@eluve/i18n';
import { BlockType } from '@eluve/llm-outputs';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';
import { areStringsEqualWithoutSpecialCharacters } from '@eluve/utils';

import { BlockDetailsFormData } from './TreeItem';
import { templateBuilderStore } from './templateBuilderStore';
import { BuilderBlock } from './utilities';

export interface BlockDetailsFormProps {
  block: BuilderBlock;
  onSubmit: () => void;
}

const BaseBlockForm: React.FC<{
  isReadonly: boolean;
  isImported?: boolean;
  onSubmit: () => void;
  id: string;
  type: BlockType;
}> = ({ isReadonly, isImported, onSubmit, id, type }) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const isAiEnabled = watch('isAiEnabled');

  return (
    <Stack gap={6}>
      {type !== 'group' && (
        <FormField
          control={control}
          name="isAiEnabled"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                {t('smartBlocks.blockDetails.fillOutBlockWith')}
              </FormLabel>
              <Select
                onValueChange={(value) => {
                  field.onChange(value === 'AI');
                  onSubmit();
                }}
                value={field.value ? 'AI' : 'Manual'}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue>
                      {field.value
                        ? t('smartBlocks.blockDetails.ai')
                        : t('smartBlocks.blockDetails.manual')}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="w-full">
                  <SelectItem
                    className="focus:bg-bg-accent-quaternary"
                    value={'AI'}
                  >
                    <div className="py-1">
                      <Text>{t('smartBlocks.blockDetails.ai')}</Text>
                      <Text color="tertiary">
                        {t('smartBlocks.blockDetails.aiOptionDescription')}
                      </Text>
                    </div>
                  </SelectItem>
                  <SelectItem
                    className="focus:bg-bg-accent-quaternary"
                    value={'Manual'}
                  >
                    <div className="py-1">
                      <Text>{t('smartBlocks.blockDetails.manual')}</Text>
                      <Text color="tertiary">
                        {t('smartBlocks.blockDetails.manualOptionDescription')}
                      </Text>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <Stack gap={3}>
        <FormField
          control={control}
          name="isRequired"
          render={({ field }) => (
            <FormItem>
              <Stack align="start" gap={4} direction="horizontal">
                <Switch
                  disabled={!isAiEnabled || isReadonly || isImported}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    onSubmit();
                  }}
                  checked={field.value}
                  id={`${id}-isRequired`}
                />
                <Stack gap={2}>
                  <FormLabel
                    className={cn('mt-1', !isAiEnabled && 'text-text-disabled')}
                    htmlFor={`${id}-isRequired`}
                  >
                    {t('smartBlocks.blockDetails.required')}
                  </FormLabel>
                  <FormDescription>
                    {t('smartBlocks.blockDetails.requiredDescription')}
                  </FormDescription>
                </Stack>
              </Stack>
              <FormMessage />
            </FormItem>
          )}
        />
        {type === 'text' && (
          <FormField
            control={control}
            name="isEmailEnabled"
            render={({ field }) => (
              <FormItem>
                <Stack align="start" gap={4} direction="horizontal">
                  <Switch
                    disabled={isReadonly}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      onSubmit();
                    }}
                    checked={field.value}
                    id={`${id}-isEmailEnabled`}
                  />
                  <Stack gap={2}>
                    <FormLabel
                      className="mt-1"
                      htmlFor={`${id}-isEmailEnabled`}
                    >
                      {t('smartBlocks.blockDetails.allowEmail')}
                    </FormLabel>
                    <FormDescription>
                      {t('smartBlocks.blockDetails.allowEmailDescription')}
                    </FormDescription>
                  </Stack>
                </Stack>
              </FormItem>
            )}
          />
        )}
      </Stack>

      {isAiEnabled ? (
        <FormField
          control={control}
          name="description"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>
                {t('smartBlocks.blockDetails.aiInstructions')}
              </FormLabel>
              <FormControl>
                <Textarea
                  className="min-h-40"
                  placeholder={t(
                    'smartBlocks.blockDetails.aiInstructionsPlaceholder',
                  )}
                  {...field}
                  disabled={isReadonly}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </Stack>
  );
};

const CheckboxBlockForm: React.FC<{
  isReadonly: boolean;
  onSubmit: () => void;
}> = ({ isReadonly, onSubmit }) => {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext<BlockDetailsFormData>();

  const checkboxOptions = watch('checkboxOptions') as string[];

  // When the length of the checkbox option changes, trigger
  // onSubmit which will try to save the blocks or set form
  // as invalid. Actual value changes are handled with 'onChange'
  useEffect(() => {
    onSubmit();
  }, [checkboxOptions.length, onSubmit]);

  return (
    <Stack gap={2}>
      <FormItem className="w-full">
        <FormLabel>{t('smartBlocks.blockDetails.checkboxes')}</FormLabel>
        <Stack gap={2}>
          {checkboxOptions.map((_, index) => (
            <FormField
              key={`checkboxOptions.${index}`}
              name={`checkboxOptions.${index}`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Stack gap={1}>
                  <FormControl>
                    <Stack gap={2} direction="horizontal">
                      <Input
                        {...field}
                        placeholder={t(
                          'smartBlocks.blockDetails.optionPlaceholder',
                          { number: index + 1 },
                        )}
                        disabled={isReadonly}
                        className={cn(
                          'flex-1',
                          error ? 'border-border-negative-strong' : undefined,
                        )}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                      />
                      {!isReadonly && (
                        <Button
                          variant="outline"
                          disabled={checkboxOptions.length === 1}
                          onClick={() => {
                            const updatedOptions = [...checkboxOptions];
                            updatedOptions.splice(index, 1);
                            setValue('checkboxOptions', updatedOptions, {
                              shouldValidate: true,
                            });
                          }}
                        >
                          <Icon name="Trash" size="auto" />
                        </Button>
                      )}
                    </Stack>
                  </FormControl>
                  <FormMessage />
                </Stack>
              )}
            />
          ))}
        </Stack>
      </FormItem>
      <Button
        variant="outline"
        onClick={() => {
          setValue(`checkboxOptions.${checkboxOptions.length}`, '', {
            shouldValidate: true,
          });
        }}
        disabled={Boolean(isReadonly)}
      >
        <Icon name="Plus" size="auto" />
        {t('smartBlocks.blockDetails.addOption')}
      </Button>
    </Stack>
  );
};

const TextBoxBlockForm: React.FC<{
  isReadonly: boolean;
  onSubmit: () => void;
}> = ({ isReadonly }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();
  return (
    <FormField
      control={control}
      name="placeholderText"
      render={({ field }) => (
        <FormItem>
          <Stack gap={2} direction="horizontal">
            <SimpleTooltip
              label={t('smartBlocks.blockDetails.placeholderTextTooltip')}
            >
              <FormLabel className="mt-2 whitespace-nowrap">
                {t('smartBlocks.blockDetails.placeholderText')}
                <Icon className="-mt-px ml-1 inline-block" name="Info" />
              </FormLabel>
            </SimpleTooltip>
          </Stack>
          <FormControl>
            <EluveEditor
              disabled={isReadonly}
              content={field.value}
              className="w-full rounded-lg border"
              onUpdate={(editor) => {
                const content = getTiptapMarkdownContentFromEditor(editor);

                if (
                  areStringsEqualWithoutSpecialCharacters(content, field.value)
                ) {
                  return;
                }
                field.onChange(content);
              }}
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};

export const BlockDetailsForm: React.FC<BlockDetailsFormProps> = ({
  onSubmit,
  block,
}) => {
  const { t } = useTranslation();
  const isReadonly = useSelector(
    templateBuilderStore,
    (store) => store.context.isReadOnly,
  );
  const isImported = useSelector(
    templateBuilderStore,
    (store) => store.context.isImported,
  );

  const { trigger, watch } = useFormContext<BlockDetailsFormData>();

  const isAiEnabled = watch('isAiEnabled');

  useMount(() => {
    trigger();
  });

  return (
    <Stack align="stretch" gap={4}>
      <BaseBlockForm
        isReadonly={isReadonly}
        isImported={isImported}
        onSubmit={onSubmit}
        id={block.id}
        type={block.type}
      />
      {!isAiEnabled && block.type === 'text' && (
        <TextBoxBlockForm
          isReadonly={isReadonly || Boolean(isImported)}
          onSubmit={onSubmit}
        />
      )}
      {block.type === 'checkbox' && (
        <CheckboxBlockForm
          isReadonly={isReadonly || Boolean(isImported)}
          onSubmit={onSubmit}
        />
      )}
      {block.importedDescription && (
        <Stack gap={2} direction="horizontal">
          <Text>{t('smartBlocks.blockDetails.importedDescription')}</Text>
          <SimpleTooltip
            label={t('smartBlocks.blockDetails.importedDescriptionTooltip')}
          >
            <Icon name="CircleHelp" />
          </SimpleTooltip>
          <Textarea disabled={true} value={block.importedDescription} />
        </Stack>
      )}
    </Stack>
  );
};
